import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal } from "./Reveal";
import { LogoMark } from "./Logo";
import { useLang, IMG, WHATSAPP } from "../lib/i18n";

export const Skincare = () => {
  const { t } = useLang();
  const s = t.skincare;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const rot = useTransform(scrollYProgress, [0, 1], [-12, 12]);
  const y = useTransform(scrollYProgress, [0, 1], ["3%", "-3%"]);

  return (
    <section id="skincare" ref={ref} data-testid="skincare-section" className="relative bg-[#14110F] text-[#F6F2EC] py-24 sm:py-36 overflow-hidden grain">
      <motion.div style={{ rotate: rot }} className="absolute -right-40 top-10 w-[520px] h-[650px] opacity-[0.07] pointer-events-none">
        <LogoMark className="w-full h-full" color="#F6F2EC" accent="#C9A66B" />
      </motion.div>
      <div className="relative max-w-[1440px] mx-auto px-5 sm:px-10 grid lg:grid-cols-12 gap-14 lg:gap-10 items-center">
        <div className="lg:col-span-5">
          <Reveal><p className="overline text-[#C9A66B] mb-8">{s.over}</p></Reveal>
          <Reveal delay={0.05}>
            <div className="flex items-center gap-5" data-testid="skincare-brand">
              <LogoMark className="w-14 h-[4.4rem]" color="#F6F2EC" accent="#C9A66B" />
              <div className="leading-none">
                <div className="font-stencil font-extralight text-6xl sm:text-7xl tracking-[0.14em]">GIOIA</div>
                <div className="font-stencil font-light text-lg tracking-[0.5em] text-[#b8ad9f] mt-1">SKINCARE</div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}><p className="mt-10 text-[#d9d0c4] font-light leading-relaxed max-w-md">{s.sub}</p></Reveal>
          <div className="mt-12 space-y-0 border-t border-[#F6F2EC]/15">
            {s.steps.map(([h, d], i) => (
              <Reveal key={h} delay={0.1 + i * 0.08}>
                <div data-testid={`skincare-step-${i}`} className="group flex gap-6 py-6 border-b border-[#F6F2EC]/15">
                  <span className="font-stencil text-[#C9A66B] tracking-[0.2em] pt-2">0{i + 1}</span>
                  <div>
                    <h3 className="font-serif-d text-3xl font-light transition-transform duration-500 group-hover:translate-x-2">{h}</h3>
                    <p className="text-sm text-[#b8ad9f] mt-1">{d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.3} className="mt-10 flex flex-wrap gap-3">
            <Link data-testid="skincare-shop-link" to="/shop" className="inline-flex rounded-full bg-[#C9A66B] px-7 py-3.5 text-[0.72rem] tracking-[0.2em] uppercase text-[#14110F] transition-colors duration-500 hover:bg-[#F6F2EC]">
              Shop
            </Link>
            <a data-testid="skincare-cta" href={WHATSAPP} target="_blank" rel="noreferrer" className="inline-flex rounded-full border border-[#C9A66B] px-7 py-3.5 text-[0.72rem] tracking-[0.2em] uppercase text-[#C9A66B] transition-colors duration-500 hover:bg-[#C9A66B] hover:text-[#14110F]">
              {s.cta}
            </a>
          </Reveal>
        </div>
        <div className="lg:col-span-6 lg:col-start-7 relative">
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-4 items-end">
            <Reveal className="sm:col-span-3">
              <div className="relative overflow-hidden aspect-[9/8] sm:aspect-square rounded-t-[120px] sm:rounded-t-[180px]" data-testid="skincare-image-main">
                <motion.img style={{ y, scale: 1.07 }} src={IMG.skincareTeam} alt="Gioia Skincare: crema viso calendula e crema viso arancia vitamina C" className="absolute inset-0 w-full h-full object-cover object-[50%_35%]" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_45%,transparent_45%,rgba(20,17,15,0.55)_100%)]" />
              </div>
            </Reveal>
            <Reveal delay={0.15} className="sm:col-span-2 sm:pb-10 grid grid-cols-2 sm:block gap-4 items-center">
              <div className="relative overflow-hidden aspect-square bg-[#F6F2EC] shadow-[0_30px_60px_-20px_rgba(0,0,0,0.6)] transition-transform duration-700 hover:-rotate-2 hover:scale-[1.03]" data-testid="skincare-image-product">
                <img src={IMG.vitc} alt="Gioia Skincare crema viso arancia vitamina C" className="absolute inset-0 w-full h-full object-cover" />
              </div>
              <p className="overline text-[#b8ad9f] mt-4">{s.line}</p>
            </Reveal>
          </div>
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            {s.products.map(([n, k, d], i) => (
              <Reveal key={n} delay={0.1 + i * 0.1}>
                <div data-testid={`skincare-product-${i}`} className="group h-full border border-[#F6F2EC]/15 p-6 transition-colors duration-500 hover:border-[#C9A66B]/60">
                  <span className="overline text-[#a9b56a] text-[0.62rem]">Nature</span>
                  <h3 className="font-serif-d text-2xl font-light mt-3">{n}</h3>
                  <p className="font-serif-d italic text-lg text-[#C9A66B]">{k}</p>
                  <p className="text-sm text-[#b8ad9f] mt-3">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
